// `station.setup.skip` — "Skip setup" (F-STN-26, ADR-0055; spec/10 §6.1, §7.5, spec/09 §6.2, spec/13 §3.3).
//
// With `stations.setupBeforePlay` on, a PC that starts while apps the venue marked "Install for players" are missing on
// it does not open for play: it is `setting_up` — the kiosk shows "Setting up this PC" — while it installs them, one after
// another, then opens (`locked`). It never stays there: a time limit (`stations.setupMaxMinutes`) opens it whatever is
// left, an app whose install fails is skipped, and staff with `stations:maintenance` may skip the rest from the console
// (`POST …/stations/{s}/setup/skip`). The api sends this command; the station
//
//   - ends its setup at once when it is `setting_up` with the setup staff saw (`startedAt`), and opens for play — an
//     installer already running is let finish (as when a session starts at an idle moment); the apps still to install
//     install later, at idle moments, as they always have;
//   - answers `not_setting_up` when it is not (it finished on its own, a session took it, or it is a later setup than the
//     one staff saw): nothing changes.
//
// It answers within the ack window (spec/09 §5.1) with `StationSetupSkipCommandResult`. A station built before this
// command answers `ok: false` `unsupported` — and it never sets up before play either, so the console never offers it one.
import { z } from "../zod.js";
import { MemberIdSchema } from "../ids/schema.js";
import { IsoDateTimeSchema } from "../primitives/scalars.js";
import { StationModeSchema } from "./station.js";
/** The command type (spec/09 §6.2). Its default expiry is five minutes (`STATION_COMMAND_EXPIRY_SECONDS`). */
export const STATION_SETUP_SKIP = 'station.setup.skip';
export const StationSetupSkipCommandPayloadSchema = z
    .object({
    /** The member who skipped it (the audit row names them too). */
    by: MemberIdSchema,
    /**
     * When the setup staff saw began (`StationSetupProgress.startedAt` on the floor), so a skip that arrives late never
     * ends a later setup; `null` when the console did not know it (the station then skips whichever setup it is in).
     */
    startedAt: IsoDateTimeSchema.nullable(),
})
    .openapi('StationSetupSkipCommandPayload', {
    description: '`station.setup.skip`: stop setting up and open this PC for play now; what is left installs later at idle moments (ADR-0055).',
});
/**
 * What the station did with the command, as its ack's `result` says:
 *
 *   skipped         it was setting up (the setup staff saw) and is open for play now
 *   not_setting_up  it was not: it had finished, a session took it, or it is in a later setup — nothing changed
 */
export const STATION_SETUP_SKIP_OUTCOMES = ['skipped', 'not_setting_up'];
export const StationSetupSkipCommandResultSchema = z
    .object({
    outcome: z.enum(STATION_SETUP_SKIP_OUTCOMES),
    /** The station's mode when it answered (`locked` after a skip). */
    mode: StationModeSchema,
})
    .openapi('StationSetupSkipCommandResult', {
    description: 'What a station did with `station.setup.skip`: skipped and open for play, or it was not setting up (ADR-0055).',
});
//# sourceMappingURL=setup.js.map